/**
 * Utilità per i controlli di autorizzazione delle pratiche
 */

/**
 * Determina se l'utente può vedere una pratica
 * @param {Object} pratica - La pratica da controllare
 * @param {Object} user - L'utente corrente
 * @param {Array} subordinateNodes - I nodi subordinati dell'utente
 * @param {boolean} isAdmin - Se l'utente è admin
 * @returns {boolean} - True se l'utente può vedere la pratica
 */
export const canUserViewPratica = (pratica, user, subordinateNodes = [], isAdmin = false, userNode = null) => {
  // Admin vede tutto - controllo multiplo per sicurezza
  if (isAdmin || user?.role === 'admin' || user?.ruolo === 'admin') {
    console.log('✅ [FRONTEND] Accesso garantito per admin:', user?.role || user?.ruolo);
    return true;
  }
  
  const userRole = user?.role || user?.ruolo || '';
  const praticaAgenteId = pratica.idAgente; // ID dell'agente assegnato alla pratica
  const praticaAgenziaId = pratica.idAgenziaPrimaria; // ID del nodo agenzia primaria
  

  
  // REGOLA PER AGENZIA: Vede tutte le pratiche della sua agenzia primaria
  if (userRole === 'agenzia' && userNode && Number(userNode.id) === Number(praticaAgenziaId)) {
    return true;
  }
  
  // Se la pratica non è assegnata a nessuno
  if (!praticaAgenteId) {
    // Solo l'agenzia primaria può vedere pratiche non assegnate della sua agenzia
    return userRole === 'agenzia' && userNode && Number(userNode.id) === Number(praticaAgenziaId);
  }
  
  // REGOLA PER AGENTI/SUBAGENTI:
  // - Se NON hanno subordinati: vedono solo le loro pratiche
  // - Se HANNO subordinati: vedono le loro + quelle dei subordinati
  if (userRole === 'agente' || userRole === 'subagente') {
    // Prima verifica se è una pratica propria
    if (user.id === praticaAgenteId) {
      return true;
    }
    
    // Se ha subordinati, verifica se la pratica appartiene a uno di loro
    if (subordinateNodes && subordinateNodes.length > 0) {
      return subordinateNodes.some(node => node.userId === praticaAgenteId || node.utenteId === praticaAgenteId);
    }
    
    // Se non ha subordinati, vede solo le sue
    return false;
  }
  
  // REGOLA PER TEAM LEADER: Vedono le loro pratiche + quelle dei subordinati
  if (userRole === 'team_leader') {
    // Vedere le proprie pratiche
    if (user.id === praticaAgenteId) {
      return true;
    }
    
    // Vedere anche le pratiche dei subordinati
    return subordinateNodes.some(node => node.userId === praticaAgenteId || node.utenteId === praticaAgenteId);
  }
  
  // Altri ruoli: logica standard con subordinati
  const isSubordinatePratica = subordinateNodes.some(node => {
    return node.userId === praticaAgenteId || node.utenteId === praticaAgenteId;
  });
  
  return isSubordinatePratica;
};

/**
 * Determina se l'utente può modificare una pratica
 * @param {Object} pratica - La pratica da controllare
 * @param {Object} user - L'utente corrente
 * @param {Array} subordinateNodes - I nodi subordinati dell'utente
 * @param {boolean} isAdmin - Se l'utente è admin
 * @returns {boolean} - True se l'utente può modificare la pratica
 */
export const canUserEditPratica = (pratica, user, subordinateNodes = [], isAdmin = false) => {
  // Admin può modificare tutto
  if (isAdmin) return true;
  
  // Solo l'agente assegnato può modificare la pratica
  return pratica.idAgente === user.id;
};

/**
 * Controlla se l'utente ha accesso a una pratica e reindirizza se necessario
 * @param {Object} pratica - La pratica da controllare
 * @param {Object} user - L'utente corrente
 * @param {Array} subordinateNodes - I nodi subordinati dell'utente
 * @param {boolean} isAdmin - Se l'utente è admin
 * @param {Function} navigate - Funzione di navigazione
 * @param {Function} toast - Funzione per mostrare notifiche
 * @returns {boolean} - True se l'utente ha accesso
 */
export const checkPraticaAccess = (pratica, user, subordinateNodes, isAdmin, navigate, toast, userNode = null) => {
  if (!canUserViewPratica(pratica, user, subordinateNodes, isAdmin, userNode)) {
    toast.error('Non hai i permessi per accedere a questa pratica');
    navigate('/nlt/pratiche');
    return false;
  }
  return true;
}; 